// Converts completed V3 writes into curated receipt copy and scalar settings values.
import { z } from 'zod';

import { shiftWriteCardSchema } from '@apps/web/features/phoebe_chat_v3/model/shift_write_card';
import { type WriteResultPresentation } from '@apps/web/features/phoebe_chat_v3/registry/types';

const writeOutputSchema = z
    .object({
        status: z.literal('executed'),
        entity: z.enum([
            'general_settings',
            'callout_settings',
            'shift_confirmation_settings',
            'care_plan_settings',
            'recommendation_defaults',
            'shifts',
            'clients',
            'caregivers',
        ]),
        verb: z.string(),
        changes: z.record(
            z.string(),
            z.object({ from: z.unknown(), to: z.unknown() }),
        ),
        warnings: z.array(z.string()),
        scope: z.enum(['phoebe_local', 'ehr']).optional(),
        shift_card: z.unknown().optional(),
        external_system_updated: z.boolean().optional(),
        destination: z.string().trim().min(1).optional(),
    })
    .strip();

const SETTINGS_TITLES = {
    general_settings: 'Organization settings updated',
    callout_settings: 'Callout settings updated',
    shift_confirmation_settings: 'Shift confirmation settings updated',
    care_plan_settings: 'Care plan settings updated',
    recommendation_defaults: 'Recommendation defaults updated',
} as const;

const SHIFT_TITLES: Readonly<Record<string, string>> = {
    add_ehr_note: 'Shift note added',
    create: 'Shift created',
    retime: 'Shift time changed',
    change_pay_rate: 'Shift pay rate changed',
    assign: 'Caregiver assigned',
    unassign: 'Caregiver removed from shift',
    reassign: 'Shift reassigned',
    cancel: 'Shift cancelled',
    update_notes: 'Shift notes updated',
};

const UNKNOWN_SHIFT_NOTE_RECEIPT: WriteResultPresentation = {
    kind: 'write_receipt',
    status: 'failed',
    title: 'Shift note status unknown',
    summary:
        'Phoebe could not safely read the result. Check the shift in the scheduling system.',
    notices: [],
};

const NOTE_ENTITIES: ReadonlySet<unknown> = new Set([
    'shifts',
    'clients',
    'caregivers',
]);

function isEhrNoteOutput(output: unknown): boolean {
    return (
        typeof output === 'object' &&
        output !== null &&
        'entity' in output &&
        NOTE_ENTITIES.has(output.entity) &&
        'verb' in output &&
        output.verb === 'add_ehr_note'
    );
}

/** Only curated copy crosses into the UI; raw fields, IDs, and errors do not. */
export function parseWriteReceipt(
    toolName: string | undefined,
    output: unknown,
): WriteResultPresentation | null {
    if (toolName !== 'write') {
        return null;
    }
    const parsed = writeOutputSchema.safeParse(output);
    if (!parsed.success) {
        return isEhrNoteOutput(output) ? UNKNOWN_SHIFT_NOTE_RECEIPT : null;
    }
    const {
        entity,
        verb,
        changes,
        warnings,
        scope,
        external_system_updated: externalSystemUpdated,
        destination,
        shift_card: shiftCard,
    } = parsed.data;
    const isShiftNote = isEhrNoteOutput(parsed.data);
    const isPersonNote = isShiftNote && entity !== 'shifts';
    const shiftTitle = Object.prototype.hasOwnProperty.call(SHIFT_TITLES, verb)
        ? SHIFT_TITLES[verb]
        : undefined;
    const isConfirmedShiftNote = isShiftNote && externalSystemUpdated === true;
    const count = Object.keys(changes).length;
    if (
        (entity === 'shifts' && !isShiftNote && shiftTitle === undefined) ||
        (isShiftNote &&
            (scope !== 'ehr' ||
                destination === undefined ||
                count !== 0 ||
                externalSystemUpdated === undefined)) ||
        (entity !== 'shifts' && !isPersonNote && verb !== 'update')
    ) {
        return isShiftNote ? UNKNOWN_SHIFT_NOTE_RECEIPT : null;
    }
    const settingsChanges =
        entity === 'shifts' || isPersonNote
            ? undefined
            : Object.fromEntries(
                  Object.entries(changes).flatMap(([key, change]) => {
                      const parsedChange = z
                          .object({
                              from: z.union([
                                  z.boolean(),
                                  z.number(),
                                  z.null(),
                              ]),
                              to: z.union([z.boolean(), z.number(), z.null()]),
                          })
                          .safeParse(change);
                      return parsedChange.success
                          ? [[key, parsedChange.data]]
                          : [];
                  }),
              );
    const parsedCard =
        entity === 'shifts' && (count > 0 || isShiftNote)
            ? shiftWriteCardSchema.safeParse(shiftCard)
            : undefined;
    return {
        kind: 'write_receipt',
        status: isShiftNote && !isConfirmedShiftNote ? 'failed' : 'succeeded',
        ...(parsedCard?.success ? { shiftCard: parsedCard.data } : {}),
        ...(settingsChanges === undefined ||
        Object.keys(settingsChanges).length === 0
            ? {}
            : { settingsChanges }),
        title:
            isShiftNote && !isConfirmedShiftNote
                ? isPersonNote
                    ? 'Activity note not confirmed'
                    : 'Shift note not confirmed'
                : count === 0 && !isShiftNote
                  ? 'No changes reported'
                  : entity === 'shifts'
                    ? (shiftTitle ?? 'Shift updated')
                    : entity === 'clients' || entity === 'caregivers'
                      ? 'Activity note added'
                      : SETTINGS_TITLES[entity],
        // An EHR warning exists because the coordinator should see it: the
        // vendor refused the write, or saved it somewhere other than the
        // surface they approved. The unconfirmed note is the exception; it
        // already reports failed with its own reason.
        notices:
            scope === 'ehr' && !(isShiftNote && !isConfirmedShiftNote)
                ? warnings
                : [],
        summary:
            isShiftNote && !isConfirmedShiftNote
                ? destination === undefined
                    ? UNKNOWN_SHIFT_NOTE_RECEIPT.summary
                    : `Phoebe could not confirm that ${destination} saved this ${isPersonNote ? 'note' : 'shift note'}.`
                : isConfirmedShiftNote && parsedCard?.success !== true
                  ? `Saved to ${destination}.`
                  : undefined,
    };
}
